<template>
    <section class="dp-layout lost-found-admin-page">
        <!-- 左侧学校树 -->
        <section class="dp-left">
            <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
        </section>

        <!-- 右侧管理员列表 -->
        <section class="dp-right">
            <template v-if="schoolId > 0">
                <!-- 顶部筛选与操作栏 -->
                <div class="filter-bar">
                    <el-form :inline="true" size="small" class="filter-form">
                        <el-form-item label="校区">
                            <el-select v-model="filterForm.campusId" placeholder="全部校区" clearable style="width: 160px;" @change="handleSearch">
                                <el-option :value="0" label="全校" />
                                <el-option v-for="item in campusOptions" :key="item.Id" :label="item.Name" :value="item.Id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="教师姓名">
                            <el-input v-model="filterForm.name" placeholder="请输入姓名或工号" clearable @keyup.enter.native="handleSearch" style="width: 180px;" />
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
                            <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
                        </el-form-item>
                    </el-form>

                    <div class="action-btn-group">
                        <el-button v-if="hasActionPermission('openAddDialog')" type="primary" size="small" icon="el-icon-plus" @click="openAddDialog">
                            新增失物招领管理员
                        </el-button>
                    </div>
                </div>

                <!-- 提示栏 -->
                <div class="tip-bar">
                    <i class="el-icon-info"></i>
                    <span>
                        被配置为失物招领管理员的教职工账号，在手机端登录后将自动获得【失物统计】等专属功能菜单。授权范围为「全校」时，可查看本校全部数据。
                    </span>
                </div>

                <!-- 数据表格 -->
                <div class="tbl-scroll-host">
                    <el-table :data="tableData" v-loading="loading" border style="width: 100%;" height="100%" class="compact-table">
                        <el-table-column type="index" label="序号" width="60" align="center" />
                        <el-table-column prop="TeacherName" label="姓名" min-width="120">
                            <template slot-scope="scope">
                                <span class="teacher-name">{{ scope.row.TeacherName || scope.row.Name || '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="TeacherAccount" label="工号/账号" min-width="120">
                            <template slot-scope="scope">
                                <span>{{ scope.row.TeacherAccount || scope.row.WorkNumber || scope.row.Account || '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="TeacherPhone" label="手机号码" min-width="130">
                            <template slot-scope="scope">
                                <span>{{ scope.row.TeacherPhone || scope.row.Phone || scope.row.ContactPhone || '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column prop="CampusName" label="授权范围" min-width="130">
                            <template slot-scope="scope">
                                <el-tag size="small" :type="isSchoolWide(scope.row) ? 'success' : 'info'">
                                    {{ formatCampusScope(scope.row) }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="Remark" label="备注" min-width="160" show-overflow-tooltip>
                            <template slot-scope="scope">
                                <span>{{ scope.row.Remark || '-' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="启用状态" width="100" align="center">
                            <template slot-scope="scope">
                                <el-switch
                                    v-if="hasActionPermission('handleToggleEnable')"
                                    v-model="scope.row.Enabled"
                                    :active-value="true"
                                    :inactive-value="false"
                                    @change="handleToggleEnable(scope.row)"
                                />
                                <el-tag v-else :type="scope.row.Enabled ? 'success' : 'info'" size="mini">
                                    {{ scope.row.Enabled ? '启用' : '停用' }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="CreateTime" label="配置时间" width="160" align="center" />
                        <el-table-column label="操作" width="200" align="center" fixed="right">
                            <template slot-scope="scope">
                                <div class="table-op-pair">
                                    <el-button v-if="hasActionPermission('openEditDialog')" type="text" size="small" class="table-op-link" @click="openEditDialog(scope.row)">编辑</el-button>
                                    <el-button v-if="hasActionPermission('handleDelete')" type="text" size="small" class="table-op-link table-op-link--danger" @click="handleDelete(scope.row)">移除</el-button>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <!-- 分页 -->
                <el-col :span="24" class="toolbar">
                    <el-pagination
                        background
                        layout="total, sizes, prev, pager, next, jumper"
                        :current-page="page"
                        :page-sizes="[10, 20, 50, 100]"
                        :page-size="pageSize"
                        :total="total"
                        @size-change="handleSizeChange"
                        @current-change="handlePageChange"
                        style="float: right;"
                    />
                </el-col>
            </template>

            <div v-else class="no-department-tip">
                <div class="no-department-tip__inner">
                    <i class="el-icon-school"></i>
                    <p>请在左侧选择所属学校，查看与维护失物招领管理员</p>
                </div>
            </div>
        </section>

        <!-- 新增管理员弹窗 -->
        <el-dialog title="新增失物招领管理员" :visible.sync="addDialogVisible" width="520px" :close-on-click-modal="false">
            <el-form :model="addForm" :rules="addRules" ref="addForm" label-width="110px">
                <el-form-item label="授权范围" prop="CampusId">
                    <el-select v-model="addForm.CampusId" placeholder="请选择授权范围" style="width: 100%;" @change="handleCandidateCampusChange">
                        <el-option :value="0" label="全校" />
                        <el-option v-for="item in campusOptions" :key="item.Id" :label="item.Name" :value="item.Id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="选择教师" prop="TeacherId">
                    <el-select
                        v-model="addForm.TeacherId"
                        filterable
                        placeholder="请输入姓名搜索选择教师"
                        style="width: 100%;"
                        :loading="loadingCandidates"
                        @change="handleSelectTeacher"
                    >
                        <el-option
                            v-for="t in candidateTeachers"
                            :key="t.TeacherId || t.Id"
                            :label="t.TeacherName || t.Name"
                            :value="t.TeacherId || t.Id"
                        />
                    </el-select>
                    <span class="sub-hint">支持按姓名从学校教职工档案中实时检索；已配置教师不会出现在候选列表中</span>
                </el-form-item>
                <el-form-item label="备注">
                    <el-input v-model="addForm.Remark" type="textarea" :rows="3" placeholder="例如：负责全校失物公示与统计" maxlength="200" show-word-limit />
                </el-form-item>
            </el-form>
            <div slot="footer" class="dialog-footer">
                <el-button @click="addDialogVisible = false">取 消</el-button>
                <el-button type="primary" :loading="submitting" @click="submitAddForm">确 定</el-button>
            </div>
        </el-dialog>

        <!-- 编辑管理员弹窗 -->
        <el-dialog title="编辑失物招领管理员" :visible.sync="editDialogVisible" width="480px" :close-on-click-modal="false">
            <el-form :model="editForm" label-width="100px" ref="editForm">
                <el-form-item label="教师姓名">
                    <span>{{ editForm.Name }}</span>
                </el-form-item>
                <el-form-item label="授权范围">
                    <el-tag size="small" :type="editForm.CampusId === 0 ? 'success' : 'info'">
                        {{ editForm.CampusId === 0 ? '全校' : (editForm.CampusName || '-') }}
                    </el-tag>
                </el-form-item>
                <el-form-item label="是否启用">
                    <el-switch v-model="editForm.Enabled" />
                </el-form-item>
                <el-form-item label="备注">
                    <el-input v-model="editForm.Remark" type="textarea" :rows="3" placeholder="请输入备注" maxlength="200" show-word-limit />
                </el-form-item>
            </el-form>
            <div slot="footer" class="dialog-footer">
                <el-button @click="editDialogVisible = false">取 消</el-button>
                <el-button type="primary" :loading="submitting" @click="submitEditForm">确 定</el-button>
            </div>
        </el-dialog>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import { getCampusList } from '../../api/modules/misc';
import { getButtonList } from '../../promissionRouter';
import {
    getLostAndFoundAdminListPage,
    getLostAndFoundCandidateTeachers,
    addLostAndFoundAdmin,
    updateLostAndFoundAdmin,
    toggleLostAndFoundAdminEnable,
    deleteLostAndFoundAdmin
} from '../../api/modules/lostAndFound';
import { normalizeDeptId, deptIdsEqual, resolveActiveSchoolId } from '../../utils/deptCascaderProps';

export default {
    name: 'LostAndFoundAdmin',
    components: { DepartmentPane },
    data() {
        return {
            schoolId: 0,
            schoolName: '',
            boundSchoolId: 0,
            buttonList: [],
            campusOptions: [],
            filterForm: {
                campusId: null,
                name: ''
            },
            tableData: [],
            page: 1,
            pageSize: 10,
            total: 0,
            loading: false,

            // 新增弹窗
            addDialogVisible: false,
            candidateTeachers: [],
            loadingCandidates: false,
            submitting: false,
            addForm: {
                CampusId: 0,
                TeacherId: null,
                Name: '',
                Remark: ''
            },
            addRules: {
                CampusId: [{
                    validator: (rule, value, callback) => {
                        // CampusId=0 表示全校，属于合法选项
                        if (value === null || value === undefined || value === '') {
                            callback(new Error('请选择授权范围'));
                        } else {
                            callback();
                        }
                    },
                    trigger: 'change'
                }],
                TeacherId: [{ required: true, message: '请选择教职工', trigger: 'change' }]
            },

            // 编辑弹窗
            editDialogVisible: false,
            editForm: {
                Id: 0,
                Name: '',
                CampusId: 0,
                CampusName: '',
                Remark: '',
                Enabled: true
            }
        };
    },
    watch: {
        '$store.state.activeSchoolId'(newSchoolId) {
            this.applySchoolChange(newSchoolId, this.$store.state.activeSchoolName);
        }
    },
    methods: {
        /** 判断当前页面是否配置了指定按钮权限 */
        hasActionPermission(func) {
            return this.buttonList.some(button => button.Func === func);
        },

        /** 解析当前学校 Id（字符串，避免雪花 Id 被 Number 截断）。最后修改：2026-10-18 */
        resolveSchoolId() {
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return fromStore;
            const local = normalizeDeptId(this.schoolId);
            if (local) return local;
            return '';
        },

        /** CampusId=0 视为全校管理员 */
        isSchoolWide(row) {
            const campusId = row.CampusId != null ? row.CampusId : row.campusId;
            return campusId === 0 || campusId === '0';
        },

        /** 表格授权范围文案 */
        formatCampusScope(row) {
            if (this.isSchoolWide(row)) return '全校';
            return row.CampusName || row.campusName || '-';
        },

        /**
         * 顶栏切换学校或左侧学校树触发时统一换校加载。最后更新：2026-09-12
         */
        applySchoolChange(schoolId, schoolName) {
            const id = normalizeDeptId(schoolId);
            if (!id) return;
            if (deptIdsEqual(id, this.schoolId) && deptIdsEqual(this.boundSchoolId, id)) return;
            this.schoolId = id;
            this.boundSchoolId = id;
            if (schoolName) this.schoolName = schoolName;
            this.filterForm.campusId = null;
            this.filterForm.name = '';
            this.page = 1;
            this.loadCampusOptions();
            this.loadTableData();
        },

        treeNodeClick(data) {
            this.applySchoolChange(data && data.value, data && data.label);
        },

        async loadCampusOptions() {
            if (!this.schoolId) return;
            try {
                const res = await getCampusList({ page: 1, intPageSize: 999, departmentId: this.schoolId });
                if (res.data && res.data.success) {
                    this.campusOptions = res.data.response.data || [];
                }
            } catch (err) {
                this.$message.error('加载校区列表失败');
            }
        },

        async loadTableData() {
            if (!this.schoolId) return;
            this.loading = true;
            try {
                const campusId = this.filterForm.campusId;
                const params = {
                    page: this.page,
                    intPageSize: this.pageSize,
                    schoolId: this.schoolId,
                    // 未选校区传空；选「全校」传 0
                    campusId: campusId === null || campusId === undefined || campusId === '' ? undefined : campusId,
                    name: this.filterForm.name || ''
                };
                const res = await getLostAndFoundAdminListPage(params);
                if (res.data && res.data.success) {
                    const resp = res.data.response;
                    this.tableData = resp.data || [];
                    this.total = resp.dataCount || 0;
                } else {
                    this.tableData = [];
                    this.total = 0;
                }
            } catch (err) {
                this.$message.error('加载失物招领管理员列表失败');
            } finally {
                this.loading = false;
            }
        },

        handleSearch() {
            this.page = 1;
            this.loadTableData();
        },

        handleReset() {
            this.filterForm.campusId = null;
            this.filterForm.name = '';
            this.page = 1;
            this.loadTableData();
        },

        handleSizeChange(val) {
            this.pageSize = val;
            this.page = 1;
            this.loadTableData();
        },

        handlePageChange(val) {
            this.page = val;
            this.loadTableData();
        },

        handleToggleEnable(row) {
            const isEnable = row.Enabled;
            const teacherName = row.TeacherName || row.Name || '该教师';
            const actionText = isEnable ? '启用' : '停用';
            const tipText = isEnable
                ? `确认启用失物招领管理员【${teacherName}】吗？启用后该教师在手机端将拥有失物统计权限。`
                : `确认停用失物招领管理员【${teacherName}】吗？停用后该教师在手机端将无法查看失物统计。`;

            this.$confirm(tipText, `${actionText}确认`, {
                type: isEnable ? 'info' : 'warning',
                confirmButtonText: `确定${actionText}`,
                cancelButtonText: '取消',
                closeOnClickModal: false
            }).then(async () => {
                try {
                    const res = await toggleLostAndFoundAdminEnable({ id: row.Id, enabled: isEnable });
                    if (res.data && res.data.success) {
                        this.$message.success(`已${actionText}失物招领管理员权限`);
                    } else {
                        row.Enabled = !isEnable;
                        this.$message.error(res.data ? res.data.msg : `${actionText}失败`);
                    }
                } catch (err) {
                    row.Enabled = !isEnable;
                    this.$message.error('网络异常，修改失败');
                }
            }).catch(() => {
                row.Enabled = !isEnable;
            });
        },

        async openAddDialog() {
            this.addForm = {
                CampusId: this.filterForm.campusId !== null && this.filterForm.campusId !== undefined && this.filterForm.campusId !== ''
                    ? this.filterForm.campusId
                    : 0,
                TeacherId: null,
                Name: '',
                Remark: ''
            };
            this.addDialogVisible = true;
            this.loadCandidates();
            this.$nextTick(() => this.$refs.addForm && this.$refs.addForm.clearValidate());
        },

        handleCandidateCampusChange() {
            this.addForm.TeacherId = null;
            this.addForm.Name = '';
            this.loadCandidates();
        },

        async loadCandidates(keyword = '') {
            if (!this.schoolId) return;
            this.loadingCandidates = true;
            try {
                const res = await getLostAndFoundCandidateTeachers({
                    schoolId: this.schoolId,
                    campusId: this.addForm.CampusId != null ? this.addForm.CampusId : 0,
                    keyword
                });
                if (res.data && res.data.success) {
                    this.candidateTeachers = res.data.response || [];
                }
            } catch (err) {
                this.candidateTeachers = [];
            } finally {
                this.loadingCandidates = false;
            }
        },

        handleSelectTeacher(val) {
            const found = this.candidateTeachers.find(t => (t.TeacherId || t.Id) === val);
            if (found) {
                this.addForm.Name = found.TeacherName || found.Name;
            }
        },

        submitAddForm() {
            this.$refs.addForm.validate(async (valid) => {
                if (!valid) return;
                this.submitting = true;
                try {
                    const payload = {
                        SchoolId: this.schoolId,
                        CampusId: this.addForm.CampusId,
                        TeacherId: this.addForm.TeacherId,
                        Name: this.addForm.Name,
                        Remark: this.addForm.Remark
                    };
                    const res = await addLostAndFoundAdmin(payload);
                    if (res.data && res.data.success) {
                        this.$message.success('新增失物招领管理员成功');
                        this.addDialogVisible = false;
                        this.loadTableData();
                    } else {
                        this.$message.error(res.data ? res.data.msg : '新增失败');
                    }
                } catch (err) {
                    this.$message.error('新增异常，请重试');
                } finally {
                    this.submitting = false;
                }
            });
        },

        openEditDialog(row) {
            this.editForm = {
                Id: row.Id,
                Name: row.TeacherName || row.Name || '',
                CampusId: row.CampusId != null ? row.CampusId : 0,
                CampusName: row.CampusName || '',
                Remark: row.Remark || '',
                Enabled: !!row.Enabled
            };
            this.editDialogVisible = true;
        },

        async submitEditForm() {
            this.submitting = true;
            try {
                const res = await updateLostAndFoundAdmin({
                    Id: this.editForm.Id,
                    Remark: this.editForm.Remark,
                    Enabled: this.editForm.Enabled
                });
                if (res.data && res.data.success) {
                    this.$message.success('修改成功');
                    this.editDialogVisible = false;
                    this.loadTableData();
                } else {
                    this.$message.error(res.data ? res.data.msg : '保存失败');
                }
            } catch (err) {
                this.$message.error('修改失败');
            } finally {
                this.submitting = false;
            }
        },

        handleDelete(row) {
            this.$confirm(`确认移除失物招领管理员【${row.TeacherName || row.Name}】吗？移除后该账号将不再具备失物招领管理员权限。`, '移除确认', {
                type: 'warning'
            }).then(async () => {
                const res = await deleteLostAndFoundAdmin({ id: row.Id });
                if (res.data && res.data.success) {
                    this.$message.success('已移除失物招领管理员');
                    this.loadTableData();
                } else {
                    this.$message.error(res.data ? res.data.msg : '移除失败');
                }
            }).catch(() => {});
        }
    },
    mounted() {
        const routers = window.localStorage.router ? JSON.parse(window.localStorage.router) : [];
        this.buttonList = getButtonList(this.$route.path, routers);
        // 学校范围页由 DepartmentPane 初始化时自动 emit tree-node-click，避免与此处重复请求
    }
};
</script>

<style scoped>
.filter-bar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.filter-form .el-form-item {
    margin-bottom: 6px;
}

.tip-bar {
    background: #f4f4f5;
    color: #606266;
    font-size: 13px;
    padding: 8px 12px;
    border-radius: 4px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    flex-shrink: 0;
}

.tip-bar i {
    color: #409EFF;
    font-size: 15px;
    margin-right: 8px;
}

.teacher-name {
    font-weight: 600;
    color: #303133;
}

.toolbar {
    margin-top: 8px;
    padding: 0;
    flex-shrink: 0;
}

.sub-hint {
    font-size: 12px;
    color: #909399;
    display: block;
    line-height: 1.4;
    margin-top: 4px;
}

.no-department-tip {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    min-height: 200px;
    color: #909399;
}

.no-department-tip i {
    font-size: 48px;
    margin-bottom: 12px;
    color: #c0c4cc;
}
</style>
